import { useEffect, useMemo, useRef, useState } from "react";

export const MARCUS_ONBOARDING_NEXT_TARGET = "marcus-onboarding-next-step";

type SpotlightRect = {
  left: number;
  top: number;
  width: number;
  height: number;
  radius: number;
};

const RECT_EPSILON = 0.5;
const SPOTLIGHT_VIEWPORT_MARGIN = 8;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

function highlightSelector(target: string): string {
  const escaped =
    typeof CSS !== "undefined" && typeof CSS.escape === "function"
      ? CSS.escape(target)
      : target.replace(/"/g, '\\"');
  return `[data-highlight-id="${escaped}"]`;
}

function getSpotlightRect(target: string | null): SpotlightRect | null {
  if (!target || typeof document === "undefined" || typeof window === "undefined") return null;
  const element = document.querySelector<HTMLElement>(highlightSelector(target));
  if (!element) return null;
  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return null;

  let pad = 10;
  let radius = 20;

  if (target === MARCUS_ONBOARDING_NEXT_TARGET) {
    pad = 8;
    radius = 999;
  } else if (target.startsWith("sidebar-nav-")) {
    pad = 4;
    radius = 16;
  }

  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const maxWidth = Math.max(0, viewportWidth - SPOTLIGHT_VIEWPORT_MARGIN * 2);
  const maxHeight = Math.max(0, viewportHeight - SPOTLIGHT_VIEWPORT_MARGIN * 2);
  const width = maxWidth > 0 ? Math.min(rect.width + pad * 2, maxWidth) : rect.width + pad * 2;
  const height = maxHeight > 0 ? Math.min(rect.height + pad * 2, maxHeight) : rect.height + pad * 2;

  return {
    left: clamp(
      rect.left - pad,
      SPOTLIGHT_VIEWPORT_MARGIN,
      viewportWidth - width - SPOTLIGHT_VIEWPORT_MARGIN,
    ),
    top: clamp(
      rect.top - pad,
      SPOTLIGHT_VIEWPORT_MARGIN,
      viewportHeight - height - SPOTLIGHT_VIEWPORT_MARGIN,
    ),
    width,
    height,
    radius,
  };
}

function spotlightRectsEqual(a: SpotlightRect | null, b: SpotlightRect | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    Math.abs(a.left - b.left) < RECT_EPSILON &&
    Math.abs(a.top - b.top) < RECT_EPSILON &&
    Math.abs(a.width - b.width) < RECT_EPSILON &&
    Math.abs(a.height - b.height) < RECT_EPSILON &&
    Math.abs(a.radius - b.radius) < RECT_EPSILON
  );
}

function useSpotlightRect(target: string | null): SpotlightRect | null {
  const [rect, setRect] = useState<SpotlightRect | null>(() => getSpotlightRect(target));
  const lastRectRef = useRef<SpotlightRect | null>(rect);

  useEffect(() => {
    if (!target || typeof window === "undefined") {
      lastRectRef.current = null;
      setRect(null);
      return;
    }

    let raf = 0;
    let active = true;

    const update = () => {
      if (!active) return;
      const next = getSpotlightRect(target);
      if (spotlightRectsEqual(lastRectRef.current, next)) return;
      lastRectRef.current = next;
      setRect(next);
    };

    const tick = () => {
      update();
      raf = window.requestAnimationFrame(tick);
    };

    update();
    raf = window.requestAnimationFrame(tick);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);

    return () => {
      active = false;
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [target]);

  return rect;
}

export function MarcusOnboardingSpotlight({
  target,
  showNextButton,
}: {
  target: string | null;
  showNextButton: boolean;
}) {
  const rect = useSpotlightRect(target);

  const spotlightStyle = useMemo(
    () =>
      rect
        ? {
            left: rect.left,
            top: rect.top,
            width: rect.width,
            height: rect.height,
            borderRadius: rect.radius,
          }
        : undefined,
    [rect],
  );

  if (!target && !showNextButton) return null;

  return (
    <div className="pointer-events-none fixed inset-0 z-[70]" aria-hidden={!showNextButton}>
      {rect ? (
        <div
          data-testid="marcus-onboarding-spotlight-frame"
          className="absolute border border-white/70 shadow-[0_0_0_9999px_rgba(15,15,15,0.42),0_0_0_1px_rgba(255,255,255,0.35),0_18px_60px_rgba(0,0,0,0.18)] transition-all duration-300"
          style={spotlightStyle}
        />
      ) : (
        <div className="absolute inset-0 bg-foreground/25 backdrop-blur-[1px]" />
      )}
    </div>
  );
}
